import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Heart, Calendar, Palette, Camera, Plus } from 'lucide-react';
import Navbar from '../../../components/Navbar.tsx';

const DesignOptions = () => {
  const navigate = useNavigate();

  const options = [
    {
      title: "Wedding Decor",
      description: "Design Your Perfect Wedding Venue",
      path: "/design/decor",
      image: "/themesOption/Rustic.jpg",
      icon: Palette,
      available: true,
      gradient: "from-pink-500 to-rose-500"
    },
    {
      title: "Florals",
      description: "Create Your Dream Floral Arrangement, from Bouquets to Boutonnieres",
      path: "/design/florals",
      image: "/themesOption/Bouquet.jpg",
      icon: Heart,
      available: true,
      gradient: "from-rose-500 to-pink-600"
    },
    {
      title: "Wedding Cake",
      description: "Craft Your Dream Wedding Cake",
      path: "/design/weddingcake",
      image: "/themesOption/Cake.jpg",
      icon: Sparkles,
      available: true,
      gradient: "from-pink-600 to-orange-500"
    },
    {
      title: "Proposal",
      description: "Bring Your Ultimate Proposal Vision to Life",
      path: "/design/proposals",
      image: "/themesOption/Proposal.jpg",
      icon: Calendar,
      available: true,
      gradient: "from-orange-500 to-rose-500"
    },
    {
      title: "Wedding Attire",
      description: "Elevate Your Style with Tailored Outfits for the Bride and Groom",
      path: "/design/attire",
      image: "/themesOption/Attire.jpg",
      icon: Camera,
      available: true,
      gradient: "from-rose-500 to-pink-500"
    },
    {
      title: "More Categories",
      description: "Exciting new design categories coming soon",
      path: "/design/morecategories",
      image: "/themesOption/Invitation.jpg",
      icon: Plus,
      available: false,
      gradient: "from-gray-400 to-gray-500"
    },
  ];

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-rose-50 to-orange-50 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ec4899' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>

        {/* Floating Elements */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-pink-400/20 rounded-full"
              animate={{
                x: [0, 100, 0],
                y: [0, -100, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 8 + i,
                repeat: Infinity,
                delay: i * 0.5,
              }}
              style={{
                left: `${10 + i * 12}%`,
                top: `${20 + i * 8}%`,
              }}
            />
          ))}
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Compact Header Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <div className="inline-flex items-center gap-2 bg-white/70 backdrop-blur-sm px-4 py-2 rounded-full border border-pink-200/60 mb-4">
              <div className="w-1.5 h-1.5 bg-pink-500 rounded-full animate-pulse" />
              <span className="text-pink-700 font-medium text-sm">AI-Powered Design Studio</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 leading-tight">
              Create Your{' '}
              <span className="bg-gradient-to-r from-pink-600 via-rose-600 to-orange-600 bg-clip-text text-transparent">
                Perfect
              </span>{' '}
              Day
            </h1>
            
            <p className="text-lg text-gray-600 max-w-xl mx-auto">
              Transform your wedding vision with intelligent design tools
            </p>
          </motion.div>

          {/* Compact Design Categories Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {options.map((option, index) => {
              const IconComponent = option.icon;
              
              return (
                <motion.div
                  key={option.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  onClick={() => option.available && navigate(option.path)}
                  className={`group relative overflow-hidden rounded-2xl transition-all duration-300 ${
                    option.available 
                      ? 'cursor-pointer hover:scale-[1.02] hover:shadow-xl' 
                      : 'opacity-75'
                  }`}
                >
                  {/* Compact Glassmorphism Card */}
                  <div className="relative bg-white/60 backdrop-blur-xl border border-white/40 rounded-2xl overflow-hidden">
                    <div className="aspect-[5/4] relative">
                      <img 
                        src={option.image} 
                        alt={option.title} 
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                      
                      {/* Compact Icon Badge */}
                      <div className={`absolute top-3 right-3 w-8 h-8 bg-gradient-to-r ${option.gradient} rounded-full flex items-center justify-center shadow-lg`}>
                        <IconComponent className="w-4 h-4 text-white" />
                      </div>
                      
                      {/* Coming Soon Overlay */}
                      {!option.available && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                          <div className="bg-white/90 backdrop-blur-sm rounded-xl px-3 py-2 border border-white/40">
                            <span className="text-pink-600 font-semibold flex items-center gap-1 text-sm">
                              <Sparkles className="w-3 h-3" />
                              Coming Soon
                            </span>
                          </div>
                        </div>
                      )}
                      
                      {/* Compact Content */}
                      <div className="absolute inset-x-0 bottom-0 p-4">
                        <h3 className="text-lg font-bold text-white mb-1">
                          {option.title}
                        </h3>
                        <p className="text-white/90 text-xs leading-relaxed line-clamp-2">
                          {option.description}
                        </p>
                      </div>
                      
                      {/* Hover Glow Effect */}
                      {option.available && (
                        <div className={`absolute inset-0 bg-gradient-to-t from-pink-500/0 to-rose-500/0 group-hover:from-pink-500/20 group-hover:to-rose-500/10 transition-all duration-300`} />
                      )}
                    </div>
                  </div>
                  
                  {/* Hover Shadow Effect */}
                  {option.available && (
                    <div className={`absolute inset-0 rounded-2xl bg-gradient-to-r ${option.gradient} opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-xl -z-10`} />
                  )}
                </motion.div>
              );
            })}
          </div>
          
          {/* Compact Bottom CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-center mt-8"
          >
            <div className="bg-white/70 backdrop-blur-xl rounded-xl p-6 border border-white/40 max-w-lg mx-auto">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Need Help Getting Started?
              </h3>
              <p className="text-gray-600 mb-4 text-sm">
                Our AI assistant can guide you through the design process
              </p>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="px-6 py-3 bg-gradient-to-r from-pink-600 to-rose-600 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 text-sm"
              >
                <span className="flex items-center justify-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Get AI Assistance
                </span>
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default DesignOptions; 